import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/pro-solid-svg-icons/faCircleQuestion';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faTruckFast } from '@fortawesome/pro-solid-svg-icons/faTruckFast';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Tooltip } from 'ui-components/components/tooltip';

import { TrackingSummaryPropType } from '../../../prop-types/tracking-summary';
import { TrackingStatus } from '../../../enums/tracking-status';

import style from './tracking-number-column.module.less';

const messages = defineMessages({
  untrackable: {
    description: 'trackingNumberColumn.untrackable',
    defaultMessage: 'The Status is Unknown',
    id: '8RqeU7',
  },
  notShipped: {
    description: 'trackingNumberColumn.notShipped',
    defaultMessage: 'Not Shipped',
    id: '5QeOCk',
  },
  delivered: {
    description: 'trackingNumberColumn.delivered',
    defaultMessage: 'Delivered',
    id: 'u1tKdB',
  },
  deliveryError: {
    description: 'trackingNumberColumn.deliveryError',
    defaultMessage: 'Delivery Exception',
    id: '5Mw28G',
  },
  shipped: {
    description: 'trackingNumberColumn.shipped',
    defaultMessage: 'In Transit',
    id: 'Z4Rirl',
  },
});

const statusToAdditionalProps = {
  [TrackingStatus.UnTrackable]: {
    icon: faCircleQuestion,
    translation: messages.untrackable,
  },
  [TrackingStatus.NotShipped]: {
    icon: faCircleQuestion,
    translation: messages.untrackable,
  },
  [TrackingStatus.Delivered]: {
    icon: faCheckCircle,
    translation: messages.delivered,
  },
  [TrackingStatus.DeliveryError]: {
    icon: faExclamationCircle,
    translation: messages.deliveryError,
  },
  [TrackingStatus.Shipped]: {
    icon: faTruckFast,
    translation: messages.shipped,
  },
};

const renderIcon = (translation, icon) => (
  <Tooltip position='bottom' text={<FormattedMessage {...translation} />}>
    <div className={style['icon-wrapper']}>
      <FontAwesomeIcon className={style.icon} icon={icon} />
    </div>
  </Tooltip>
);

const renderTrackingNumber = (translation, icon, url, trackingNumber, shouldRenderIcon) => {
  return (
    <div className={style['normal-content']}>
      {shouldRenderIcon && renderIcon(translation, icon)}
      <div className={style.message}>
        {url ? (
          <Button href={url} onClick={(e) => e.stopPropagation()} theme='link'>
            {trackingNumber}
          </Button>
        ) : (
          trackingNumber
        )}
      </div>
    </div>
  );
};

const TrackingNumberColumn = ({ trackingSummary, shouldRenderIcon = true }) => {
  if (!trackingSummary) {
    return null;
  }
  const { trackingNumber, url } = trackingSummary;
  const status = TrackingStatus[trackingSummary.status] || TrackingStatus.UnTrackable;
  const additional = statusToAdditionalProps[status];

  if (!additional) {
    return null;
  }

  const { icon, translation } = additional;

  return (
    <div
      className={classnames(style['tracking-number-column'], {
        [style['has-error']]: status === TrackingStatus.DeliveryError,
      })}
    >
      {trackingNumber && renderTrackingNumber(translation, icon, url, trackingNumber, shouldRenderIcon)}
    </div>
  );
};

/**
 * @param {object} props
 * @param {import('../../../prop-types/tracking-summary').default} props.trackingSummary
 * @param {boolean} [props.shouldRenderIcon]
 * @returns {JSX.Element | null}
 */
TrackingNumberColumn.propTypes = {
  trackingSummary: TrackingSummaryPropType,
  shouldRenderIcon: PropTypes.bool,
};

export default React.memo(TrackingNumberColumn);
